import { MutationTree } from 'vuex';
import { IContentMutationPayload } from './types/mutations';
import {
  ICreateNodeRequest,
  IUpdateNodeRequest,
  IDeleteNodeRequest,
  IMoveNodesRequest,
  ICreateRowRequest,
  IDeleteRowRequest,
  IMoveRowRequest,
  IUpdateCellsRequest,
  IMoveCellRequest,
  UpdateRowRequest,
} from '@/editor/api/services/types/requests';
import { ISection, IRow, ICell, INode, IEditorBlock, CustomMergeTagPayload } from '@/editor/types';
import { EEditorContentMutation } from '@/editor/editor-block/store/editor-content.constants';
import { IEditorContentState } from '@/editor/editor-block/store/editor-content.types';
import { ContentMutationHelper } from '@/editor/editor-block/store/helpers/content-mutation.helper';

export const mutations: MutationTree<IEditorContentState> = {
  [EEditorContentMutation.UpdateCustomTagEverywhere](
    state: IEditorContentState,
    payload: CustomMergeTagPayload
  ) {
    state.blocks = ContentMutationHelper.cascadeUpdateCustomMergeTags(state.blocks, payload);
  },

  [EEditorContentMutation.ReplaceBlock](state: IEditorContentState, newBlock: IEditorBlock) {
    state.blocks = state.blocks.map((block: IEditorBlock) =>
      block.contentId === newBlock.contentId ? newBlock : block
    );
  },

  [EEditorContentMutation.CreateNode](
    state: IEditorContentState,
    {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      append: appendNode,
      payload,
    }: ICreateNodeRequest
  ) {
    const node: INode = ContentMutationHelper.getNewNode(payload);

    const createPayload: IContentMutationPayload = {
      updateSection: ContentMutationHelper.mapSection,
      updateRow: ContentMutationHelper.mapRow,
      updateCell: ContentMutationHelper.mapCell,
      updateNode: ContentMutationHelper.addNodes,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      appendNode,
      nodes: [node],
    };

    state.blocks = state.blocks.map((block: IEditorBlock) =>
      ContentMutationHelper.mapBlock(block, createPayload)
    );
  },

  [EEditorContentMutation.UpdateNode](
    state: IEditorContentState,
    {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      payload,
    }: IUpdateNodeRequest
  ) {
    const updatePayload: IContentMutationPayload = {
      updateSection: ContentMutationHelper.mapSection,
      updateRow: ContentMutationHelper.mapRow,
      updateCell: ContentMutationHelper.mapCell,
      updateNode: ContentMutationHelper.updateNode,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      update: payload,
    };

    state.blocks = state.blocks.map((block: IEditorBlock) =>
      ContentMutationHelper.mapBlock(block, updatePayload)
    );
  },

  [EEditorContentMutation.MoveNodes](
    state: IEditorContentState,
    {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      sourceSectionId,
      sourceRowId,
      sourceCellId,
      sourceNodeIds,
      append: appendNode,
    }: IMoveNodesRequest
  ) {
    const nodes: INode[] =
      state.blocks
        .find((block: IEditorBlock) => block.contentId === contentId)
        ?.content.sections.find(({ id }: ISection) => id === sourceSectionId)
        ?.rows.find(({ id }: IRow) => id === sourceRowId)
        ?.cells.find(({ id }: ICell) => id === sourceCellId)
        ?.nodes.filter(({ id }: INode) => sourceNodeIds.includes(id)) || [];

    if (!nodes.length) {
      return;
    }

    const deletePayload: IContentMutationPayload = {
      updateSection: ContentMutationHelper.mapSection,
      updateRow: ContentMutationHelper.mapRow,
      updateCell: ContentMutationHelper.mapCell,
      updateNode: ContentMutationHelper.deleteNodes,
      contentId,
      targetSectionId: sourceSectionId,
      targetRowId: sourceRowId,
      targetCellId: sourceCellId,
      targetNodeIds: sourceNodeIds,
    };

    const blocksAfterDelete: IEditorBlock[] = state.blocks.map(block =>
      ContentMutationHelper.mapBlock(block, deletePayload)
    );

    const addPayload: IContentMutationPayload = {
      updateSection: ContentMutationHelper.mapSection,
      updateRow: ContentMutationHelper.mapRow,
      updateCell: ContentMutationHelper.mapCell,
      updateNode: ContentMutationHelper.addNodes,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      appendNode,
      nodes,
    };

    state.blocks = blocksAfterDelete.map((block: IEditorBlock) =>
      ContentMutationHelper.mapBlock(block, addPayload)
    );
  },

  [EEditorContentMutation.UpdateCells](
    state: IEditorContentState,
    { contentId, targetSectionId, targetRowId, payload }: IUpdateCellsRequest
  ) {
    const updateCells = (row: IRow, { cellsUpdate }: IContentMutationPayload): IRow => {
      return row.id === targetRowId
        ? {
            ...row,
            cells: row.cells.map((cell: ICell) => {
              const cellToUpdate = cellsUpdate.find(({ cellId }) => cellId === cell.id);
              return cellToUpdate
                ? {
                    ...cell,
                    width: cellToUpdate.width,
                    type: cellToUpdate.cellType,
                    nodes: cell.nodes,
                  }
                : cell;
            }),
          }
        : row;
    };

    const updatePayload: IContentMutationPayload = {
      updateSection: ContentMutationHelper.mapSection,
      updateRow: updateCells,
      contentId,
      targetSectionId,
      targetRowId,
      cellsUpdate: payload,
    };

    state.blocks = state.blocks.map((block: IEditorBlock) =>
      ContentMutationHelper.mapBlock(block, updatePayload)
    );
  },

  [EEditorContentMutation.MoveCell](
    state: IEditorContentState,
    {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      sourceSectionId,
      sourceRowId,
      sourceCellId,
      append: appendCell = true,
    }: IMoveCellRequest
  ) {
    const cell: ICell = state.blocks
      .find((block: IEditorBlock) => block.contentId === contentId)
      ?.content.sections.find(({ id }: ISection) => id === sourceSectionId)
      ?.rows.find(({ id }: IRow) => id === sourceRowId)
      ?.cells.find(({ id }: ICell) => id === sourceCellId);

    if (!cell) {
      return;
    }

    const deletePayload: IContentMutationPayload = {
      updateSection: ContentMutationHelper.mapSection,
      updateRow: ContentMutationHelper.deleteCell,
      contentId,
      targetSectionId: sourceSectionId,
      targetRowId: sourceRowId,
      targetCellId: sourceCellId,
    };

    const blocksAfterDelete: IEditorBlock[] = state.blocks.map(block =>
      ContentMutationHelper.mapBlock(block, deletePayload)
    );

    const addPayload: IContentMutationPayload = {
      updateSection: ContentMutationHelper.mapSection,
      updateRow: ContentMutationHelper.addCell,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      appendCell,
      cell,
    };

    state.blocks = blocksAfterDelete.map((block: IEditorBlock) =>
      ContentMutationHelper.mapBlock(block, addPayload)
    );
  },

  [EEditorContentMutation.MoveRow](
    state: IEditorContentState,
    {
      contentId,
      targetSectionId,
      targetRowId,
      sourceSectionId,
      sourceRowId,
      append: appendRow,
    }: IMoveRowRequest
  ) {
    const target = state.blocks
      .find((block: IEditorBlock) => block.contentId === contentId)
      ?.content.sections.find(({ id }: ISection) => id === targetSectionId);

    if (!target) {
      return;
    }

    const row: IRow = state.blocks
      .find((block: IEditorBlock) => block.contentId === contentId)
      ?.content.sections.find(({ id }: ISection) => id === sourceSectionId)
      ?.rows.find(({ id }: IRow) => id === sourceRowId);

    if (!row) {
      return;
    }

    const deletePayload: IContentMutationPayload = {
      updateSection: ContentMutationHelper.deleteRow,
      contentId,
      targetSectionId: sourceSectionId,
      targetRowId: sourceRowId,
    };

    const blocksAfterDelete: IEditorBlock[] = state.blocks.map(block =>
      ContentMutationHelper.mapBlock(block, deletePayload)
    );

    const addPayload: IContentMutationPayload = {
      updateSection: ContentMutationHelper.addRow,
      contentId,
      targetSectionId,
      targetRowId,
      appendRow,
      row,
    };

    state.blocks = blocksAfterDelete.map((block: IEditorBlock) =>
      ContentMutationHelper.mapBlock(block, addPayload)
    );
  },

  [EEditorContentMutation.UpdateRow](
    state: IEditorContentState,
    { contentId, targetRowId, targetSectionId, payload: { cells, columnCount } }: UpdateRowRequest
  ) {
    const updateRow = (row: IRow): IRow => {
      if (row.id === targetRowId) {
        return {
          ...row,
          columnCount,
          cells: cells.map(({ cellId, cellType, width, nodes }) => ({
            id: cellId,
            type: cellType,
            width,
            nodes: nodes.map(node => {
              const { nodeType: type, nodeId: id, ...rest } = node;
              return { ...rest, id, type } as INode;
            }),
          })),
        };
      }
      return row;
    };

    const payload: IContentMutationPayload = {
      updateSection: ContentMutationHelper.mapSection,
      updateRow,
      contentId,
      targetSectionId,
      targetRowId,
    };

    state.blocks = state.blocks.map((block: IEditorBlock) =>
      ContentMutationHelper.mapBlock(block, payload)
    );
  },

  [EEditorContentMutation.DeleteNode](
    state: IEditorContentState,
    { contentId, targetSectionId, targetRowId, targetCellId, targetNodeId }: IDeleteNodeRequest
  ) {
    const payload: IContentMutationPayload = {
      updateSection: ContentMutationHelper.mapSection,
      updateRow: ContentMutationHelper.mapRow,
      updateCell: ContentMutationHelper.mapCell,
      updateNode: ContentMutationHelper.deleteNodes,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeIds: [targetNodeId],
    };

    state.blocks = state.blocks.map((block: IEditorBlock) =>
      ContentMutationHelper.mapBlock(block, payload)
    );
  },

  [EEditorContentMutation.SetBlock](state: IEditorContentState, block: IEditorBlock) {
    state.blocks = [...state.blocks, block];
  },

  [EEditorContentMutation.RemoveBlock](state: IEditorContentState, id: string) {
    state.blocks = state.blocks.filter(block => block.id !== id);
  },

  [EEditorContentMutation.ResetBlocks](state: IEditorContentState) {
    state.blocks = [];
  },

  [EEditorContentMutation.CreateRow](
    state: IEditorContentState,
    { contentId, targetSectionId, targetRowId, append: appendRow, payload }: ICreateRowRequest
  ) {
    const row: IRow = ContentMutationHelper.getNewRow(payload);

    const createPayload: IContentMutationPayload = {
      updateSection: ContentMutationHelper.addRow,
      contentId,
      targetSectionId,
      targetRowId,
      appendRow,
      row,
    };

    state.blocks = state.blocks.map((block: IEditorBlock) =>
      ContentMutationHelper.mapBlock(block, createPayload)
    );
  },

  [EEditorContentMutation.DeleteRow](
    state: IEditorContentState,
    { contentId, targetSectionId, targetRowId }: IDeleteRowRequest
  ) {
    const payload: IContentMutationPayload = {
      updateSection: ContentMutationHelper.deleteRow,
      contentId,
      targetSectionId,
      targetRowId,
    };

    state.blocks = state.blocks.map((block: IEditorBlock) =>
      ContentMutationHelper.mapBlock(block, payload)
    );
  },
};
